import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { APP_CONFIG, AppConfig } from '@src/app.config';
import { RequestService } from '@src/app/shared/services/http/request/request.service';
import { map, Observable } from 'rxjs';
import {UserProfile} from "@app/areas/users/models/user-profile.interface";

/* ───────────────────────────────────────────
 * Shared DTOs (mirrors your backend contracts)
 * ─────────────────────────────────────────── */
export interface ConversationDto {
  id: string;
  title: string;
  creatorPublicKeyB64: string;
  participants: string[];
  wrappedKeys: Record<string, Record<string, string>>;
  lastMessage: MessageDto | null;
  // server may include this for the current device (optional):
  unwrappedGroupKey?: string | null;
}

export interface MessageDto {
  id: string;
  senderId: string;
  ciphertextB64: string;
  createdAt: number; // unix seconds
}

export interface PagedResultDto<T> {
  items: T[];
  page: number;
  pageSize: number;
  hasNext: boolean;
}

export interface CreateConversationInput {
  title: string;
  creatorPublicKeyB64: string;
  envelopes: Record<string, Record<string, string>>; // userId -> deviceId -> raw AES envelope (client-wrapped)
  participantIds: string[];
  slug?: string | null;
  rawGroupKeyB64?: string | null;
}

export interface AddParticipantDevicesInput {
  conversationId: string;
  userId: string;
  deviceEnvelopes: Record<string, string>; // deviceId -> raw AES envelope
}

export interface SendMessageInputDto {
  ciphertextB64: string;
}

export interface PublicKeyDto {
  deviceId: string;
  publicKeyB64: string;
}

export interface UserKeysResponseDto {
  userId: string;
  devices: { deviceId: string; publicKeyB64: string; createdAt: number }[];
}

export interface EnvelopeDto {
  // when present, it's already unwrapped for the current device by server/KMS
  unwrappedGroupKey?: string | null;
  // or, if your server returns kms-wrapped envelopes, adapt here accordingly
}

export interface EnvelopeUpdateDto {
  conversationId: string;
  userId: string;
  deviceId: string;
  envelopeB64: string; // raw AES envelope (client-wrapped to device's X25519)
}

export interface SecureBackupKdfParamsDto {
  algorithm: string;
  n: number;
  r: number;
  p: number;
  memoryKb: number;
  dkLen: number;
}

export interface SecureBackupBlobDto {
  userId: string;
  deviceId?: string | null;
  blobVersion: number;
  kdfSaltB64: string;
  kdfParams: SecureBackupKdfParamsDto;
  aeadNonceB64: string;
  ciphertextB64: string;
  createdAt: number;
  updatedAt: number;
}

export interface SecureBackupAttemptDto {
  kind: string; // "pin"
  success: boolean;
}

export interface SecureBackupAttemptResponseDto {
  failureCount: number;
  lockUntil: number;
  retryAfterSeconds: number;
}

export interface ChatAttachmentDto {
  id: string;
  conversationId: string;
  senderId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  width?: number;
  height?: number;
  createdAt: number;
  objectKey: string;
  declaredMimeType?: string;
  declaredExtension?: string;
  clientScanVersion?: string;
  clientScanVerdict?: 'pass' | 'warn' | 'fail';
  clientScanReasons?: string[];
  sha256Plaintext?: string;
  isPotentiallyUnsafe?: boolean;
}

export interface ChatAttachmentSignedUrlDto {
  url: string | null;
}

export interface ConsolidatedPersonPendingDto {
  otherIdentityId: string;
  conversationId?: string | null;
  unreadChat: boolean;
  careTeamPending: boolean;
  hasPending: boolean;
  lastMessageAt?: number | null;
}

export interface ConsolidatedNotificationsDto {
  people: ConsolidatedPersonPendingDto[];
  globalPendingCount: number;
  unreadChatPeopleCount: number;
  careTeamPendingPeopleCount: number;
  generatedAt: string;
}

/* ───────────────────────────────────────────
 * Service
 * ─────────────────────────────────────────── */
@Injectable({ providedIn: 'root' })
export class ChatApiService {
  private readonly base: string;

  constructor(
    private request: RequestService,
    private http: HttpClient,
    @Inject(APP_CONFIG) private appConfig: AppConfig,
  ) {
    this.base = `${this.appConfig.angularEndpoint}/chat`;
  }

  /* ─────────────── Conversations (iOS ABP app service) ─────────────── */
  /** GET /ios/chat/conversationWithUser?otherUserId=... */
  getConversationWithUser(otherUserId: string): Observable<ConversationDto | null> {
    const url = `${this.base}/conversationWithUser/${encodeURIComponent(otherUserId)}`;
    return this.request.get(url).pipe(map(r => (r.body as ConversationDto) ?? null));
  }

  /** GET /ios/chat/conversation/{conversationId} */
  getConversation(conversationId: string): Observable<ConversationDto> {
    const url = `${this.base}/conversation/${encodeURIComponent(conversationId)}`;
    return this.request.get(url).pipe(map(r => r.body as ConversationDto));
  }

  /** GET /ios/chat/conversation/{conversationId} */
  getConversationBySlug(slug: string, userId: string): Observable<ConversationDto> {
    const url = `${this.base}/conversationBySlug/${userId}?slug=${slug}`;
    return this.request.get(url).pipe(map(r => r.body as ConversationDto));
  }

  /** GET /ios/chat/participantDetails/{conversationId} */
  getParticipants(convId: string): Observable<UserProfile[]> {
    const url = `${this.base}/participantDetails/${convId}`;
    return this.request.get(url).pipe(map(r => r.body as UserProfile[]));
  }

  /** GET /ios/chat/conversation/{conversationId} */
  joinConversation(conversationId: string, deviceId: string, envelopeB64?: string): Observable<ConversationDto> {
    const url = `${this.base}/joinConversation/${conversationId}`;
    return this.request.post(url, {deviceId, envelopeB64}).pipe(map(r => r.body as ConversationDto));
  }

  /** POST /ios/chat/conversation */
  createConversation(input: CreateConversationInput): Observable<ConversationDto> {
    const url = `${this.base}/conversation`;
    return this.request.post(url, input).pipe(map(r => r.body as ConversationDto));
  }

  /** POST /ios/chat/addParticipantDevices */
  addParticipantDevices(input: AddParticipantDevicesInput): Observable<ConversationDto> {
    const url = `${this.base}/addParticipantDevices`;
    return this.request.post(url, input).pipe(map(r => r.body as ConversationDto));
  }

  /** GET /ios/chat/conversationsMe?page=&pageSize= */
  listMyConversations(page = 1, pageSize = 50): Observable<PagedResultDto<ConversationDto>> {
    const url = `${this.base}/conversationsMe?page=${page}&pageSize=${pageSize}`;
    return this.request.get(url).pipe(map(r => r.body as PagedResultDto<ConversationDto>));
  }

  /** POST /ios/chat/sendMessage/{convId} */
  sendMessage(convId: string, ciphertextB64: string): Observable<void> {
    const payload: SendMessageInputDto = {
      ciphertextB64
    }
    const url = `${this.base}/sendMessage/${encodeURIComponent(convId)}`;
    return this.request.post(url, payload).pipe(map(() => void 0));
  }

  markConversationRead(conversationId: string, seenAt?: number): Observable<void> {
    const url = `${this.base}/markConversationRead/${encodeURIComponent(conversationId)}`;
    return this.request.post(url, seenAt ? { seenAt } : {}).pipe(map(() => void 0));
  }

  /** GET /ios/chat/messages/{conversationId}?page=&pageSize= */
  listMessages(conversationId: string, page = 1, pageSize = 100): Observable<PagedResultDto<MessageDto>> {
    const url = `${this.base}/messages/${encodeURIComponent(conversationId)}?page=${page}&pageSize=${pageSize}`;
    console.log('ListMessages', url)
    return this.request.get(url).pipe(map(r => r.body as PagedResultDto<MessageDto>));
  }

  /** POST /ios/chat/rotatePublicKey */
  rotatePublicKey(input: PublicKeyDto): Observable<string[]> {
    const url = `${this.base}/rotatePublicKey`;
    return this.request.post(url, input).pipe(map(r => r.body as string[]));
  }

  /** GET /ios/chat/userKeys/{userId} */
  getUserKeys(userId: string): Observable<UserKeysResponseDto | null> {
    const url = `${this.base}/userKeys/${encodeURIComponent(userId)}`;
    return this.request.get(url).pipe(map(r => (r.body as UserKeysResponseDto) ?? null));
  }

  /** GET /ios/chat/envelope/{conversationId} */
  getEnvelope(conversationId: string): Observable<EnvelopeDto> {
    const url = `${this.base}/envelope/${encodeURIComponent(conversationId)}`;
    return this.request.get(url).pipe(map(r => r.body as EnvelopeDto));
  }

  /** PUT /ios/chat/envelope */
  updateEnvelope(input: EnvelopeUpdateDto): Observable<void> {
    const url = `${this.base}/envelope`;
    return this.request.put(url, input).pipe(map(() => void 0));
  }

  /* ─────────────── Secure Backup (PIN Restore) ─────────────── */
  /** GET /ang/chat/secureBackup */
  getSecureBackup(): Observable<SecureBackupBlobDto | null> {
    const url = `${this.base}/secureBackup`;
    return this.request.get(url).pipe(map(r => (r.body as SecureBackupBlobDto) ?? null));
  }

  /** POST /ang/chat/secureBackup */
  upsertSecureBackup(input: SecureBackupBlobDto): Observable<SecureBackupBlobDto> {
    const url = `${this.base}/secureBackup`;
    return this.request.post(url, input).pipe(map(r => r.body as SecureBackupBlobDto));
  }

  /** DELETE /ang/chat/secureBackup */
  deleteSecureBackup(): Observable<void> {
    const url = `${this.base}/secureBackup`;
    return this.request.delete(url).pipe(map(() => void 0));
  }

  /** POST /ang/chat/secureBackupAttempt */
  recordSecureBackupAttempt(input: SecureBackupAttemptDto): Observable<SecureBackupAttemptResponseDto> {
    const url = `${this.base}/secureBackupAttempt`;
    return this.request.post(url, input).pipe(map(r => r.body as SecureBackupAttemptResponseDto));
  }

  /* ─────────────── Attachments ─────────────── */
  uploadAttachment(conversationId: string, form: FormData): Observable<ChatAttachmentDto> {
    const url = `${this.base}/uploadAttachment/${encodeURIComponent(conversationId)}`;
    return this.http.post<ChatAttachmentDto>(url, form);
  }

  listAttachments(conversationId: string, page = 1, pageSize = 50): Observable<PagedResultDto<ChatAttachmentDto>> {
    const url = `${this.base}/attachments/${encodeURIComponent(conversationId)}?page=${page}&pageSize=${pageSize}`;
    return this.request.get(url).pipe(map(r => r.body as PagedResultDto<ChatAttachmentDto>));
  }

  getAttachmentSignedUrl(conversationId: string, attachmentId: string): Observable<ChatAttachmentSignedUrlDto> {
    const url = `${this.base}/attachmentSignedUrl?conversationId=${encodeURIComponent(conversationId)}&attachmentId=${encodeURIComponent(attachmentId)}`;
    return this.request.get(url).pipe(map(r => r.body as ChatAttachmentSignedUrlDto));
  }

  downloadAttachment(conversationId: string, attachmentId: string): Observable<Blob> {
    const url = `${this.base}/attachmentDownload?conversationId=${encodeURIComponent(conversationId)}&attachmentId=${encodeURIComponent(attachmentId)}`;
    return this.http.get(url, { responseType: 'blob', headers: new HttpHeaders({ Accept: '*/*' }) }) as Observable<Blob>;
  }

  getConsolidatedNotifications(): Observable<ConsolidatedNotificationsDto> {
    const url = `${this.appConfig.angularEndpoint}/notifications/consolidated`;
    return this.request.get(url).pipe(map(r => r.body as ConsolidatedNotificationsDto));
  }
}
